/**
 * sample-gift.js
 * ダーマロジカ：無料サンプル自動付与ロジック
 *
 * 処理フロー:
 * 1. カート更新イベント（ThemeEvents.cartUpdate = 'cart:update'）をフック
 * 2. window.sampleRules（theme.liquidでLiquidから注入）のルール一覧を評価
 * 3. 不要になったサンプルを削除 → 不足しているサンプルを追加（差分方式）
 * 4. サンプル商品の削除ボタン・数量変更UIを非表示
 * 5. サンプルのみカートにある場合、決済ボタンを無効化
 */

(function () {
  let isProcessing = false;
  let lastCartData = null;
  const originalFetch = window.fetch;

  function extractId(val) {
    if (!val) return '';
    const str = String(val);
    if (str.startsWith('gid://')) {
      const parts = str.split('/');
      return parts[parts.length - 1];
    }
    return str;
  }

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // =============================
  // MutationObserver
  // morphSection によるDOM再描画後に validateSampleOnly / hideSampleControls を再適用する
  // =============================
  function setupMutationObserver() {
    const reapply = debounce(() => {
      if (!lastCartData) return;
      validateSampleOnly(lastCartData);
      hideSampleControls(lastCartData);
    }, 150);

    document.querySelectorAll('cart-items-component').forEach(el => {
      new MutationObserver(reapply).observe(el, { childList: true, subtree: true });
    });
  }

  // =============================
  // Fetchインターセプター（カート追加・変更・削除の同期処理）
  // /cart/add.js    : 商品追加時にサンプルを付与
  // /cart/change.js : 商品削除・数量変更時に不要サンプルを除去
  // =============================
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');

    // ── /cart/change.js（削除・数量変更）──
    if (url.includes('/cart/change.js') || url.includes('/cart/change')) {
      const response = await originalFetch(input, init);
      if (!response.ok) return response;

      const clone = response.clone();
      try {
        const responseText = await clone.text();
        const changeData = JSON.parse(responseText);
        // changeData は変更後のカート全体（items, total_price, sections を含む）

        const sampleItems = getSampleItemsToRemove(changeData);

        if (sampleItems.length > 0) {
          // sections はカンマ区切り文字列で渡す（配列不可）
          const sectionsStr = changeData.sections ? Object.keys(changeData.sections).join(',') : '';
          let lastData = null;

          // item.key（ラインアイテム固有キー）で確実に削除する
          for (let i = 0; i < sampleItems.length; i++) {
            const { key, quantity } = sampleItems[i];
            const isLast = i === sampleItems.length - 1;
            const body = { id: key, quantity };
            // 最後の削除リクエストにのみ sections を付与 → 最新HTMLを一括取得
            if (isLast && sectionsStr) body.sections = sectionsStr;

            const removeRes = await originalFetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            });

            if (removeRes.ok) lastData = await removeRes.json();
          }

          if (lastData) {
            lastCartData = lastData;
            validateSampleOnly(lastData);
            hideSampleControls(lastData);

            // Content-Encoding 等の元レスポンスヘッダーを引き継がず、
            // Content-Type のみ明示指定（gzip等の混入を防ぐ）
            return new Response(
              JSON.stringify(lastData),
              { status: response.status, headers: { 'Content-Type': 'application/json' } }
            );
          }
        }
      } catch (e) {
        console.error('[SampleGift] /cart/change.js intercept error:', e);
      }

      return response;
    }

    // ── /cart/add.js（商品追加）──
    if (url.includes('/cart/add.js') || url.includes('/cart/add')) {
      const response = await originalFetch(input, init);

      if (!response.ok) {
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('text/html')) {
            return new Response(JSON.stringify({
              status: response.status,
              message: 'エラーが発生しました',
              description: response.status === 429
                ? '短時間に多数のリクエストが送信されたため、一時的に制限されています。数分待ってから再度お試しください。'
                : 'リクエスト処理中にエラーが発生しました。再度お試しください。'
            }), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        } catch (e) {}
        return response;
      }

      const clone = response.clone();
      try {
        const addedData = await clone.json();

        const cartRes = await originalFetch('/cart.js');
        const cartData = await cartRes.json();

        const samplesToAdd = getSamplesToAdd(cartData);

        if (samplesToAdd.length > 0) {
          let sections = '';
          if (init && init.body) {
            if (init.body instanceof FormData) {
              sections = init.body.get('sections') || '';
            } else {
              try {
                const bodyJson = JSON.parse(init.body);
                if (bodyJson.sections) {
                  sections = Array.isArray(bodyJson.sections) ? bodyJson.sections.join(',') : bodyJson.sections;
                }
              } catch (e) {}
            }
          }

          const addBody = { items: samplesToAdd };
          if (sections) addBody.sections = sections;

          const addRes = await originalFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(addBody)
          });

          if (addRes.ok) {
            const addResData = await addRes.json();

            const finalCartRes = await originalFetch('/cart.js');
            const finalCartData = await finalCartRes.json();

            lastCartData = finalCartData;
            hideSampleControls(finalCartData);
            validateSampleOnly(finalCartData);

            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: finalCartData,
                data: { source: 'sample-gift', itemCount: finalCartData.item_count }
              }
            }));

            if (sections && addResData.sections) {
              const mergedData = { ...addedData, sections: addResData.sections };
              return new Response(JSON.stringify(mergedData), {
                status: response.status,
                headers: { 'Content-Type': 'application/json' }
              });
            }
          }
        }
      } catch (e) {
        console.error('[SampleGift] Fetch intercept error:', e);
      }

      return response;
    }

    return originalFetch(input, init);
  };

  // =============================
  // メイン初期化
  // =============================
  function initSampleGift() {
    document.addEventListener('cart:update', async function (event) {
      if (isProcessing) return;
      if (event.detail?.data?.source === 'sample-gift') return;

      let cartData = event.detail?.resource;

      if (!cartData || !cartData.items) {
        try {
          const res = await originalFetch('/cart.js');
          cartData = await res.json();
        } catch (e) {
          console.error('[SampleGift] カートデータ再取得エラー:', e);
          return;
        }
      }

      isProcessing = true;
      try {
        lastCartData = cartData;
        await processSampleRules(cartData);
        validateSampleOnly(lastCartData);
        hideSampleControls(lastCartData);
      } finally {
        isProcessing = false;
      }
    });

    setupMutationObserver();
    fetchCartAndProcess();
  }

  // =============================
  // 初回読み込み時のカート取得
  // =============================
  async function fetchCartAndProcess() {
    try {
      const res = await originalFetch('/cart.js');
      const cartData = await res.json();
      lastCartData = cartData;
      await processSampleRules(cartData);
      validateSampleOnly(lastCartData);
      hideSampleControls(lastCartData);
    } catch (e) {
      console.error('[SampleGift] 初回カート取得エラー:', e);
    }
  }

  // =============================
  // cart-items-component の sectionId を収集してセクションHTMLを取得
  // sectionRenderer のキャッシュを介さず直接 fetch することで確実に最新を反映
  // =============================
  async function fetchFreshSections() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach(el => {
      if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    if (sectionIds.size === 0) return null;

    const freshSections = {};
    await Promise.all([...sectionIds].map(async (sectionId) => {
      try {
        const url = `${window.location.pathname}?section_id=${sectionId}`;
        const res = await originalFetch(url);
        if (res.ok) freshSections[sectionId] = await res.text();
      } catch (e) {
        console.error('[SampleGift] section fetch error:', sectionId, e);
      }
    }));

    return Object.keys(freshSections).length > 0 ? freshSections : null;
  }

  // =============================
  // ルール評価 → 不要サンプル削除 → 必要サンプル追加
  // =============================
  async function processSampleRules(cartData) {
    // 1. 条件が外れたサンプルを削除
    const sampleItems = getSampleItemsToRemove(cartData);

    if (sampleItems.length > 0) {
      try {
        // item.key で確実に削除（variant_id 方式より信頼性が高い）
        for (const { key, quantity } of sampleItems) {
          await originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity })
          });
        }

        const cartRes = await originalFetch('/cart.js');
        cartData = await cartRes.json();
        lastCartData = cartData;
      } catch (e) {
        console.error('[SampleGift] サンプル削除エラー:', e);
      }
    }

    // 2. 条件を満たすサンプルを追加
    const samplesToAdd = getSamplesToAdd(cartData);

    if (samplesToAdd.length === 0) {
      if (sampleItems.length > 0) {
        // 削除後のDOM更新：sectionRenderer のキャッシュを経由せず直接 morphSection させるため
        // 新しいセクションHTMLを取得して event.detail.data.sections に載せる
        const freshSections = await fetchFreshSections();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            data: {
              source: 'sample-gift',
              itemCount: cartData.item_count,
              sections: freshSections || undefined
            }
          }
        }));
      }
      return;
    }

    try {
      await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: samplesToAdd })
      });

      const cartRes = await originalFetch('/cart.js');
      const updatedCartData = await cartRes.json();
      lastCartData = updatedCartData;

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: updatedCartData,
          data: { source: 'sample-gift', itemCount: updatedCartData.item_count }
        }
      }));
    } catch (e) {
      console.error('[SampleGift] サンプル追加エラー:', e);
    }
  }

  // =============================
  // 現在のカートに対して必要なサンプル数を計算する共通処理
  // =============================
  function computeRequiredCount(cartData) {
    const rules = window.sampleRules;
    if (!rules || rules.length === 0) return {};

    const now = new Date();
    const requiredCount = {};

    for (const rule of rules) {
      if (rule.start_date) {
        const startDate = new Date(rule.start_date.includes('T') ? rule.start_date : `${rule.start_date}T00:00:00+09:00`);
        if (startDate > now) continue;
      }
      if (rule.end_date) {
        const endDate = new Date(rule.end_date.includes('T') ? rule.end_date : `${rule.end_date}T23:59:59+09:00`);
        if (endDate < now) continue;
      }
      if (!rule.sample_product_id) continue;
      if (!evaluateRule(rule, cartData)) continue;

      const vId = extractId(rule.sample_variant_id || rule.sample_product_id);
      const qty = parseInt(rule.quantity) || 1;
      requiredCount[vId] = (requiredCount[vId] || 0) + qty;
    }

    return requiredCount;
  }

  // =============================
  // 追加が必要なサンプルを返す（現在数との差分）
  // =============================
  function getSamplesToAdd(cartData) {
    const requiredCount = computeRequiredCount(cartData);
    const samples = [];

    for (const vId in requiredCount) {
      const currentQty = cartData.items
        .filter(item => item.properties && item.properties._is_sample === 'true'
          && extractId(item.variant_id) === vId)
        .reduce((sum, item) => sum + item.quantity, 0);
      const diff = requiredCount[vId] - currentQty;
      if (diff > 0) {
        samples.push({
          id: vId,
          quantity: diff,
          properties: { _is_sample: 'true' }
        });
      }
    }
    return samples;
  }

  // =============================
  // 削除が必要なサンプルを返す（条件が外れたもの・過剰なもの）
  // 戻り値: [{ key, quantity }] — item.key で確実にラインアイテムを特定する
  // /cart/update.js の variant_id 方式は同一variant複数時に不確実なため非使用
  // =============================
  function getSampleItemsToRemove(cartData) {
    const requiredCount = computeRequiredCount(cartData);
    const items = [];

    for (const item of cartData.items) {
      if (!item.properties || item.properties._is_sample !== 'true') continue;

      const vId = extractId(item.variant_id);
      const required = requiredCount[vId] || 0;

      if (item.quantity > required) {
        items.push({ key: item.key, quantity: required });
      }
    }

    return items;
  }

  // =============================
  // 条件判定ロジック
  // =============================
  function evaluateRule(rule, cartData) {
    const triggerType = rule.trigger_type;
    // Shopify Ajax APIは金額を100倍（セント相当）で返すため閾値も100倍して比較
    const minAmount = parseInt(rule.min_amount) * 100;

    // サンプル商品を除いた合計金額（amount系トリガーの閾値比較に使用）
    // total_price にサンプルの価格が含まれると削除条件を誤判定するため
    const nonSampleTotal = cartData.items.reduce((sum, item) => {
      if (item.properties && item.properties._is_sample === 'true') return sum;
      return sum + item.line_price;
    }, 0);

    if (!triggerType || triggerType === 'product') {
      const targetIds = rule.trigger_product_ids ? [...rule.trigger_product_ids] : [];
      if (rule.trigger_product_id) targetIds.push(rule.trigger_product_id);
      if (targetIds.length > 0) {
        return cartData.items.some(item =>
          !item.properties?._is_sample &&
          targetIds.some(id => extractId(item.product_id) === extractId(id))
        );
      }
    }

    if (triggerType === 'amount' || (!triggerType && rule.min_amount)) {
      return nonSampleTotal >= minAmount;
    }

    if (triggerType === 'product_and_amount') {
      const targetIds = rule.trigger_product_ids ? [...rule.trigger_product_ids] : [];
      if (rule.trigger_product_id) targetIds.push(rule.trigger_product_id);
      const hasProduct = cartData.items.some(item =>
        !item.properties?._is_sample &&
        targetIds.some(id => extractId(item.product_id) === extractId(id))
      );
      return hasProduct && nonSampleTotal >= minAmount;
    }

    if (triggerType === 'collection' && rule.trigger_collection_id) {
      const colProductIds = (window.sampleCollectionProducts &&
        window.sampleCollectionProducts[extractId(rule.trigger_collection_id)]) || [];
      return cartData.items.some(item =>
        !item.properties?._is_sample &&
        colProductIds.some(id => extractId(item.product_id) === extractId(id))
      );
    }

    if (triggerType === 'quantity') {
      const targetIds = rule.trigger_product_ids || [];
      const minQty = parseInt(rule.trigger_min_quantity) || 1;
      const totalQty = cartData.items
        .filter(item =>
          !item.properties?._is_sample &&
          targetIds.some(id => extractId(item.product_id) === extractId(id))
        )
        .reduce((sum, item) => sum + item.quantity, 0);
      return totalQty >= minQty;
    }

    if (triggerType === 'multiple_products') {
      const targetIds = rule.trigger_product_ids || [];
      if (targetIds.length === 0) return false;
      return targetIds.every(id =>
        cartData.items.some(item =>
          !item.properties?._is_sample && extractId(item.product_id) === extractId(id)
        )
      );
    }

    return false;
  }

  // =============================
  // サンプル行に data-is-sample 属性を付与（CSS制御用）
  // morphSection でDOM更新後も MutationObserver 経由で再適用される
  // =============================
  function hideSampleControls(cartData) {
    if (!cartData || !cartData.items) return;

    const sampleKeys = new Set(
      cartData.items
        .filter(item => item.properties && item.properties._is_sample === 'true')
        .map(item => item.key)
    );

    document.querySelectorAll('.cart-items__table-row[data-key]').forEach(row => {
      if (sampleKeys.has(row.dataset.key)) {
        row.setAttribute('data-is-sample', 'true');
      } else {
        row.removeAttribute('data-is-sample');
      }
    });
  }

  // =============================
  // サンプル単体決済ブロック
  // カートページ・カートドロワーは同じ cart-summary.liquid スニペットを使用しており
  // どちらも id="checkout" / name="checkout" で統一されている
  // =============================
  function validateSampleOnly(cartData) {
    const checkoutBtns = document.querySelectorAll('button[name="checkout"], #checkout');
    if (checkoutBtns.length === 0) return;

    const items = cartData.items || [];
    if (items.length === 0) return;

    const allSamples = items.every(item =>
      item.properties && item.properties._is_sample === 'true'
    );

    checkoutBtns.forEach(btn => {
      if (allSamples) {
        btn.disabled = true;
        btn.setAttribute('title', 'サンプル商品のみでのご購入はできません');

        let errorEl = btn.parentNode.querySelector('.sample-gift-error');
        if (!errorEl) {
          errorEl = document.createElement('p');
          errorEl.className = 'sample-gift-error';
          errorEl.style.cssText = 'color: var(--color-primary); font-size:1.4rem; margin-top:8px; text-align:center;';
          btn.parentNode.insertBefore(errorEl, btn.nextSibling);
        }
        errorEl.textContent = 'サンプル商品のみでのご購入はできません。';
      } else {
        btn.disabled = false;
        btn.removeAttribute('title');
        const errorEl = btn.parentNode.querySelector('.sample-gift-error');
        if (errorEl) errorEl.remove();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSampleGift);
  } else {
    initSampleGift();
  }
})();
